/** Allowed shape for an auto-appended extension — alphanumeric, max 12 chars. Rejects polyglot/long-token smuggling. */
export const SAFE_EXTENSION_REGEX = /^[a-z0-9]{1,12}$/

/** Allow-list of MIME types we'll auto-derive an extension from. Unknown MIMEs return undefined rather than guess. */
export const CONTENT_TYPE_TO_EXTENSION: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'image/tiff': 'tif',
  'image/gif': 'gif',
  'image/x-canon-cr2': 'cr2',
  'image/x-nikon-nef': 'nef',
  'image/x-sony-arw': 'arw',
  'image/x-adobe-dng': 'dng',
  'image/x-fuji-raf': 'raf',
  'image/x-olympus-orf': 'orf',
  'application/pdf': 'pdf',
  'application/zip': 'zip',
  'application/x-zip-compressed': 'zip',
}

/** Returns true when the filename ends with a `.ext` segment (excluding leading-dot hidden files and trailing dots). */
export function hasFilenameExtension(filename: string): boolean {
  const lastDot = filename.lastIndexOf('.')
  return lastDot > 0 && lastDot < filename.length - 1
}

/** Extracts the extension (without dot, lowercased) from a URL's pathname; rejects if it doesn't match SAFE_EXTENSION_REGEX. */
export function getExtensionFromUrl(url: string): string | undefined {
  let pathname: string
  try {
    pathname = new URL(url).pathname
  } catch {
    return undefined
  }

  const lastSlash = pathname.lastIndexOf('/')
  const lastDot = pathname.lastIndexOf('.')
  if (lastDot <= lastSlash || lastDot === pathname.length - 1) return undefined

  const candidate = pathname.slice(lastDot + 1).toLowerCase()
  return SAFE_EXTENSION_REGEX.test(candidate) ? candidate : undefined
}

/** Maps a `Content-Type` response header value to a file extension via CONTENT_TYPE_TO_EXTENSION. */
export function getExtensionFromContentType(contentType: string | null | undefined): string | undefined {
  if (!contentType) return undefined
  const mime = contentType.split(';')[0].trim().toLowerCase()
  const extension = CONTENT_TYPE_TO_EXTENSION[mime]
  return extension && SAFE_EXTENSION_REGEX.test(extension) ? extension : undefined
}

/** Appends an extension derived from the URL pathname (preferred) or Content-Type when `filename` lacks one. */
export function ensureFilenameExtension(
  filename: string,
  sources: { url?: string, contentType?: string | null }
): string {
  if (hasFilenameExtension(filename)) return filename

  const extension =
    (sources.url && getExtensionFromUrl(sources.url)) ||
    getExtensionFromContentType(sources.contentType)

  return extension ? `${filename}.${extension}` : filename
}
